import type {
  BlockContent,
  CommentsContent,
  CustomContent,
  CustomTextContent,
  ScopeOfWorkContent,
  TermsConditionsContent
} from '@/blocks/domain/types';
import { customUsesSections } from './customSections';

type ProseBlockContent = ScopeOfWorkContent | CommentsContent | TermsConditionsContent | CustomTextContent | CustomContent;

const PROSE_KINDS = new Set<BlockContent['kind']>(['scope_of_work', 'comments', 'terms_conditions', 'custom_text', 'custom']);

/**
 * A prose block (Terms, Project scope, Comments, Custom text, Custom) is two page elements in the
 * block editor (LLAMS-610 DB-05, Figma `15:39` / `11:69`): its Heading (`title`) and its Text
 * (`bodyHtml`). These ids are the shared canvas ↔ Sections-panel selection ids for them; prose has
 * no `fields` list, so they can never collide with a field id.
 */
export const PROSE_HEADING_ELEMENT_ID = 'prose:heading';
export const PROSE_TEXT_ELEMENT_ID = 'prose:text';

/** Kinds whose body is written on the quote, not in the block (D-15): the block editor shows a placeholder. */
const QUOTE_BOUND_PROSE_KINDS = new Set(['scope_of_work']);

export const QUOTE_BOUND_PROSE_PLACEHOLDER = 'This will be filled in the Quote builder';

export function isQuoteBoundProseKind(kind: string): boolean {
  return QUOTE_BOUND_PROSE_KINDS.has(kind);
}

/** A Custom block is prose only while it is the legacy `{ title, bodyHtml }` shape; sectioned, its elements are fields. */
function isProseContent(content: BlockContent): content is ProseBlockContent {
  if (content.kind === 'custom') return !customUsesSections(content);
  return PROSE_KINDS.has(content.kind);
}

/** Writes a prose element's edited value back into the block content; `null` when the id is not a prose element of it. */
export function applyProseElementValue(content: BlockContent, elementId: string, value: string): BlockContent | null {
  if (!isProseContent(content)) return null;
  if (elementId === PROSE_HEADING_ELEMENT_ID) return { ...content, title: value };
  if (elementId === PROSE_TEXT_ELEMENT_ID) return { ...content, bodyHtml: value };
  return null;
}
